import { Link } from "react-router-dom";
import { ArrowRight, Lock, ShowerHead, Quote } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useSpaData } from "../context/SpaDataContext";
import { useBooking } from "../context/BookingContext";
import { Reveal } from "../components/Reveal";
import { TherapistCard } from "../components/TherapistCard";
import { MassageCard } from "../components/MassageCard";
import { Seo } from "../components/Seo";
import { waLink, imgUrl } from "../lib/spa";
import { WhatsAppIcon } from "../components/WhatsAppIcon";

const HERO_IMG =
  "https://images.unsplash.com/photo-1761470575018-135c213340eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBzcGElMjBkYXJrJTIwbW9vZHxlbnwwfHx8fDE3ODExMTkyNzZ8MA&ixlib=rb-4.1.0&q=85";
const FALLBACK_SPACE_1 =
  "/api/files/magnolia-lunar/cdn/massages/massagem-relaxante/cover-1773411247527.jpg";
const FALLBACK_SPACE_2 =
  "/api/files/magnolia-lunar/cdn/massages/massagem-tantrica/cover-1773411888989.jpg";

export default function Home() {
  const { t, ml } = useLanguage();
  const { therapists, massages, spacePhotos } = useSpaData();
  const { openBooking } = useBooking();
  const featured = massages.filter((m) => m.featured);
  const SPACE_1 = spacePhotos?.[0]?.url || FALLBACK_SPACE_1;
  const SPACE_2 = spacePhotos?.[1]?.url || FALLBACK_SPACE_2;

  return (
    <div data-testid="home-page">
      <Seo title={t("seo.home.title")} description={t("seo.home.desc")} />
      {/* HERO */}
      <section className="relative flex min-h-screen items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-background" />
        </div>
        <div className="relative z-10 mx-auto max-w-4xl px-5 text-center">
          <Reveal>
            <p className="text-[10px] uppercase tracking-[0.5em] text-gold sm:text-xs">{t("hero.label")}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <h1 className="mt-6 font-display text-5xl font-light leading-[1.05] text-white sm:text-6xl lg:text-7xl">
              {t("hero.title1")}
              <br />
              <em className="font-medium italic text-gold">{t("hero.title2")}</em>
            </h1>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base">
              {t("hero.subtitle")}
            </p>
          </Reveal>
          <Reveal delay={0.45}>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <button
                data-testid="hero-book-button"
                onClick={() => openBooking()}
                className="w-full rounded-full bg-gold px-10 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] sm:w-auto"
              >
                {t("hero.ctaBook")}
              </button>
              <Link
                data-testid="hero-explore-link"
                to="/massagens"
                className="w-full rounded-full border border-white/30 px-10 py-4 text-xs uppercase tracking-[0.25em] text-white transition-all duration-300 hover:border-gold hover:text-gold sm:w-auto"
              >
                {t("hero.ctaExplore")}
              </Link>
            </div>
          </Reveal>
        </div>
        <div className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-center">
          <p className="text-[9px] uppercase tracking-[0.4em] text-white/40">{t("hero.scroll")}</p>
          <div className="mx-auto mt-3 h-10 w-px animate-pulse bg-gradient-to-b from-gold to-transparent" />
        </div>
      </section>

      {/* THERAPISTS */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <Reveal>
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.45em] text-gold">{t("team.label")}</p>
              <h2 className="mt-3 font-display text-4xl font-light text-foreground sm:text-5xl">{t("team.title")}</h2>
              <p className="mt-4 max-w-lg text-sm text-foreground/72">{t("team.subtitle")}</p>
            </div>
            <Link
              data-testid="home-all-therapists-link"
              to="/terapeutas"
              className="group inline-flex shrink-0 items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold"
            >
              {t("team.viewAll")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
            </Link>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3">
          {therapists.map((th, i) => (
            <Reveal key={th.slug} delay={(i % 3) * 0.1}>
              <TherapistCard therapist={th} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* MASSAGES */}
      <section className="border-y border-foreground/5 bg-secondary">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
          <Reveal>
            <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.45em] text-gold">{t("services.label")}</p>
                <h2 className="mt-3 font-display text-4xl font-light text-foreground sm:text-5xl">{t("services.title")}</h2>
                <p className="mt-4 max-w-lg text-sm text-foreground/72">{t("services.subtitle")}</p>
              </div>
              <Link
                data-testid="home-all-massages-link"
                to="/massagens"
                className="group inline-flex shrink-0 items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold"
              >
                {t("services.viewAll")}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {featured.map((m, i) => (
              <Reveal key={m.slug} delay={i * 0.12}>
                <MassageCard massage={m} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ESSENCE */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <div className="relative">
              <div className="overflow-hidden rounded-2xl">
                <img src={imgUrl(SPACE_1)} alt="Interior Magnolia Lunar" className="aspect-[4/5] w-full object-cover" loading="lazy" />
              </div>
              <div className="absolute -bottom-10 -right-4 hidden w-2/5 overflow-hidden rounded-2xl border-4 border-background sm:block">
                <img src={imgUrl(SPACE_2)} alt="Detalhe do espaço" className="aspect-square w-full object-cover" loading="lazy" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-[10px] uppercase tracking-[0.45em] text-gold">{t("essence.label")}</p>
            <h2 className="mt-3 font-display text-4xl font-light leading-tight text-foreground sm:text-5xl">
              {t("essence.title1")} <em className="italic text-gold">{t("essence.title2")}</em>
            </h2>
            <p className="mt-6 text-sm leading-relaxed text-foreground/65 sm:text-base">{t("essence.p1")}</p>
            <p className="mt-4 text-sm leading-relaxed text-foreground/65 sm:text-base">{t("essence.p2")}</p>
            <div className="mt-10 grid grid-cols-2 gap-6">
              <div className="rounded-xl border border-foreground/8 bg-card p-5">
                <Lock className="h-5 w-5 text-gold" strokeWidth={1.5} />
                <h4 className="mt-3 font-display text-lg text-foreground">{t("essence.privacy")}</h4>
                <p className="mt-1 text-xs text-foreground/55">{t("essence.privacyDesc")}</p>
              </div>
              <div className="rounded-xl border border-foreground/8 bg-card p-5">
                <ShowerHead className="h-5 w-5 text-gold" strokeWidth={1.5} />
                <h4 className="mt-3 font-display text-lg text-foreground">{t("essence.comfort")}</h4>
                <p className="mt-1 text-xs text-foreground/55">{t("essence.comfortDesc")}</p>
              </div>
            </div>
            <Link
              data-testid="home-about-link"
              to="/sobre"
              className="group mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold"
            >
              {t("essence.cta")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-y border-foreground/5 bg-secondary">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
          <Reveal>
            <h2 className="text-center font-display text-4xl font-light text-foreground sm:text-5xl">
              {t("testimonials.title")}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {t("testimonials.items").map((item, i) => (
              <Reveal key={i} delay={i * 0.12}>
                <figure className="flex h-full flex-col rounded-2xl border border-foreground/8 bg-card p-8">
                  <Quote className="h-6 w-6 text-gold/40" strokeWidth={1.5} />
                  <blockquote className="mt-5 flex-1 font-display text-lg font-light italic leading-relaxed text-foreground/82">
                    “{item.text}”
                  </blockquote>
                  <figcaption className="mt-6 text-xs uppercase tracking-[0.25em] text-gold">
                    {item.author}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-4xl px-5 py-24 text-center sm:py-32">
          <Reveal>
            <p className="text-[10px] uppercase tracking-[0.45em] text-gold">{t("contact.label")}</p>
            <h2 className="mt-3 font-display text-4xl font-light text-foreground sm:text-5xl">{t("contact.title")}</h2>
            <p className="mx-auto mt-5 max-w-xl text-sm text-foreground/72">{t("contact.readyDesc")}</p>
            <a
              data-testid="home-whatsapp-cta"
              href={waLink(t("booking.generalMessage"))}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-10 inline-flex items-center gap-3 rounded-full bg-gold px-10 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_30px_rgba(212,175,55,0.3)]"
            >
              <WhatsAppIcon className="h-5 w-5" />
              {t("contact.whatsappCta")}
            </a>
            <div className="mt-8 flex items-center justify-center gap-3 text-xs text-foreground/55">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-gold" />
              </span>
              {t("contact.availability")} · {t("contact.fewSlots")}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
